import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { CAP } from '../utils/permissions';
import { usePermissions, ReadOnlyNotice } from '../utils/PermissionContext';
import { errorText } from '../utils/apiErrors';
import { authHeaders } from '../utils/apiAuth';
import { getVatDetails } from '../utils/vat';

const unwrapField = (val) => {
  let current = val;
  while (current && typeof current === 'object' && !Array.isArray(current)) {
    if ('stringValue' in current) {
      current = current.stringValue;
    } else if ('integerValue' in current) {
      current = current.integerValue;
    } else if ('doubleValue' in current) {
      current = current.doubleValue;
    } else if ('booleanValue' in current) {
      current = current.booleanValue;
    } else if ('value' in current) {
      current = current.value;
    } else {
      return '';
    }
  }
  return current ?? '';
};

const normalizeRecord = (r) => {
  if (!r || typeof r !== 'object') return {};
  const cleaned = {};
  Object.keys(r).forEach((k) => {
    cleaned[k] = unwrapField(r[k]);
  });
  cleaned.debtor_id = String(cleaned.debtor_id || cleaned.afas_debtor_number || '').trim();
  cleaned.debtor_name = String(cleaned.debtor_name || cleaned.invoice_by_name || '').trim();
  cleaned.customer_name = String(cleaned.customer_name || cleaned.debtor_name || '').trim();
  cleaned.domain = String(cleaned.domain || '').trim();
  cleaned.po_number = String(cleaned.po_number || '').trim();
  cleaned.invoice_by = String(cleaned.invoice_by || '').trim();
  cleaned.country = String(cleaned.country || '').trim();
  cleaned.vatCode = String(cleaned.vatCode || '').trim();
  cleaned.key = String(cleaned.key || '').trim();
  cleaned.summary = String(cleaned.summary || '').trim();
  cleaned.emailAddress = String(cleaned.emailAddress || '').trim();
  cleaned.authorName = String(cleaned.authorName || '').trim();
  cleaned.authorPicture = String(cleaned.authorPicture || cleaned.authorAvatarUrl || '').trim();
  cleaned.reporterEmail = String(cleaned.reporterEmail || '').trim();
  cleaned.created_date = String(cleaned.created_date || '').trim();
  cleaned.factuurtekst = String(cleaned.factuurtekst || cleaned.text || '').trim();
  cleaned.text = cleaned.factuurtekst;
  cleaned.cost = Number(cleaned.cost ?? cleaned.price ?? 0) || 0;
  cleaned.created_order_id = cleaned.created_order_id ? String(cleaned.created_order_id).trim() : null;
  cleaned.customfield_10068 = cleaned.customfield_10068 ? String(cleaned.customfield_10068).trim() : null;
  return cleaned;
};

const AdminJira = ({ googleIdToken, t }) => {
  const { can } = usePermissions();
  const canWrite = can(CAP.ADMIN_WRITE);

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'debtor_id', direction: 'asc' });
  const [selectedRecords, setSelectedRecords] = useState(new Set());
  const [sendingToAfas, setSendingToAfas] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [selectedDebtors, setSelectedDebtors] = useState(new Set());
  const [selectedInvoiceBy, setSelectedInvoiceBy] = useState(new Set());
  const [selectedAuthors, setSelectedAuthors] = useState(new Set());
  const [viewMode, setViewMode] = useState('ready_to_invoice'); // 'ready_to_invoice' | 'all' | 'do_not_invoice' | 'unlinked' | 'internal'

  // Default to previous month YYYY-MM
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    now.setDate(1);
    now.setMonth(now.getMonth() - 1);
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  const apiUrl = process.env.REACT_APP_API_URL || '';

  const fetchJiraData = async (syncInline = false) => {
    if (syncInline) {
      setSyncing(true);
    } else {
      setLoading(true);
    }
    setError(null);
    try {
      const syncQuery = syncInline ? '&sync=true' : '';
      const response = await fetch(`${apiUrl}/api/admin/jira-billing?month=${selectedMonth}${syncQuery}`, {
        headers: authHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorText(errorData, t, 'Failed to fetch Jira billing data'));
      }

      const data = await response.json();
      const normalized = Array.isArray(data) ? data.map(normalizeRecord) : [];
      setRecords(normalized);
      if (syncInline) {
        setSuccessMessage(`Synced ${normalized.length} Jira worklog record(s) for ${selectedMonth}.`);
      }
    } catch (err) {
      setError(err.message || 'Error loading Jira billing data');
    } finally {
      setLoading(false);
      setSyncing(false);
    }
  };

  useEffect(() => {
    if (googleIdToken) {
      fetchJiraData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, googleIdToken, selectedMonth]);

  useEffect(() => {
    setSelectedRecords(new Set());
  }, [viewMode, selectedMonth]);

  const isDoNotInvoice = (r) => Boolean(r.customfield_10068 !== null && r.customfield_10068 !== undefined && String(r.customfield_10068).trim() !== '') || Boolean(r.doNotInvoice);

  const counts = useMemo(() => {
    let ready = 0;
    let doNotInvoiceCount = 0;
    let unlinked = 0;
    let internal = 0;
    records.forEach((r) => {
      const debtorStr = String(r.debtor_id || r.afas_debtor_number || '').trim();
      const hasDebtor = Boolean(debtorStr);
      const isInternal = debtorStr === '15802';
      const dni = isDoNotInvoice(r);
      const hasCost = Number(r.cost ?? r.price ?? 0) !== 0;

      if (hasDebtor && !isInternal && !dni && hasCost) {
        ready++;
      }
      if (dni) {
        doNotInvoiceCount++;
      }
      if (!hasDebtor) {
        unlinked++;
      }
      if (isInternal) {
        internal++;
      }
    });
    return {
      ready,
      doNotInvoiceCount,
      unlinked,
      internal,
      all: records.length,
    };
  }, [records]);

  const uniqueDebtors = useMemo(() => {
    const debtors = new Set(
      records
        .map((r) => {
          const dId = String(r.debtor_id || r.afas_debtor_number || '').trim();
          const dName = r.debtor_name || r.invoice_by_name || '';
          return dId ? `${dId} - ${dName}`.trim() : 'Unlinked';
        })
        .filter(Boolean)
    );
    return Array.from(debtors).sort();
  }, [records]);

  const uniqueInvoiceBy = useMemo(() => {
    const list = new Set(records.map((r) => r.invoice_by).filter(Boolean));
    return Array.from(list).sort();
  }, [records]);

  const uniqueAuthors = useMemo(() => {
    const list = new Set(records.map((r) => r.emailAddress).filter(Boolean));
    return Array.from(list).sort();
  }, [records]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const groupKeyForRecord = (r) => {
    const dId = String(r.debtor_id || r.afas_debtor_number || '').trim();
    if (dId) return dId;
    return `unlinked-${r.domain || 'unknown'}`;
  };

  const filteredAndSortedRecords = useMemo(() => {
    return records
      .filter((rec) => {
        const debtorStr = String(rec.debtor_id || rec.afas_debtor_number || '').trim();
        const hasDebtor = Boolean(debtorStr);
        const isInternal = debtorStr === '15802';
        const dni = isDoNotInvoice(rec);
        const hasCost = Number(rec.cost ?? rec.price ?? 0) !== 0;

        if (viewMode === 'ready_to_invoice') {
          if (!hasDebtor || isInternal || dni || !hasCost) return false;
        } else if (viewMode === 'do_not_invoice') {
          if (!dni) return false;
        } else if (viewMode === 'unlinked') {
          if (hasDebtor) return false;
        } else if (viewMode === 'internal') {
          if (!isInternal) return false;
        }

        if (selectedDebtors.size > 0) {
          const dName = rec.debtor_name || rec.invoice_by_name || '';
          const key = debtorStr ? `${debtorStr} - ${dName}`.trim() : 'Unlinked';
          if (!selectedDebtors.has(key)) return false;
        }

        if (selectedInvoiceBy.size > 0) {
          if (!selectedInvoiceBy.has(rec.invoice_by)) return false;
        }

        if (selectedAuthors.size > 0) {
          if (!selectedAuthors.has(rec.emailAddress)) return false;
        }

        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();
        return (
          (rec.key && rec.key.toLowerCase().includes(term)) ||
          (rec.summary && rec.summary.toLowerCase().includes(term)) ||
          (rec.domain && rec.domain.toLowerCase().includes(term)) ||
          (rec.emailAddress && rec.emailAddress.toLowerCase().includes(term)) ||
          (rec.reporterEmail && rec.reporterEmail.toLowerCase().includes(term)) ||
          (rec.debtor_id && String(rec.debtor_id).toLowerCase().includes(term)) ||
          (rec.debtor_name && rec.debtor_name.toLowerCase().includes(term)) ||
          (rec.customer_name && rec.customer_name.toLowerCase().includes(term)) ||
          (rec.po_number && rec.po_number.toLowerCase().includes(term)) ||
          (rec.created_order_id && rec.created_order_id.toLowerCase().includes(term)) ||
          (rec.factuurtekst && rec.factuurtekst.toLowerCase().includes(term)) ||
          (rec.text && rec.text.toLowerCase().includes(term)) ||
          (rec.invoice_by && rec.invoice_by.toLowerCase().includes(term))
        );
      })
      .sort((a, b) => {
        let valA = a[sortConfig.key];
        let valB = b[sortConfig.key];

        if (sortConfig.key === 'cost') {
          valA = Number(a.cost ?? a.price ?? 0);
          valB = Number(b.cost ?? b.price ?? 0);
        }

        if (typeof valA === 'number' && typeof valB === 'number') {
          return sortConfig.direction === 'asc' ? valA - valB : valB - valA;
        }

        valA = (valA || '').toString().toLowerCase();
        valB = (valB || '').toString().toLowerCase();

        if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
        if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
        // Secondary sort by issue key
        const keyA = (a.key || '').toLowerCase();
        const keyB = (b.key || '').toLowerCase();
        if (keyA < keyB) return -1;
        if (keyA > keyB) return 1;
        return 0;
      });
  }, [records, searchTerm, selectedDebtors, selectedInvoiceBy, selectedAuthors, sortConfig, viewMode]);

  const recordId = (r) => r.id || `${r.key}-${r.worklogAccountId}-${r.created}`;

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = filteredAndSortedRecords.map(recordId);
      setSelectedRecords(new Set(allIds));
    } else {
      setSelectedRecords(new Set());
    }
  };

  const handleSelectRecord = (id) => {
    const newSelected = new Set(selectedRecords);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRecords(newSelected);
  };

  const handleSelectGroup = (gKey, checked) => {
    const newSelected = new Set(selectedRecords);
    filteredAndSortedRecords.forEach((r) => {
      if (groupKeyForRecord(r) === gKey) {
        const id = recordId(r);
        if (checked) {
          newSelected.add(id);
        } else {
          newSelected.delete(id);
        }
      }
    });
    setSelectedRecords(newSelected);
  };

  const isGroupFullySelected = (gKey) => {
    const groupRecords = filteredAndSortedRecords.filter((r) => groupKeyForRecord(r) === gKey);
    if (groupRecords.length === 0) return false;
    return groupRecords.every((r) => selectedRecords.has(recordId(r)));
  };

  const handleSyncJira = async () => {
    setSyncing(true);
    setSuccessMessage('');
    setError(null);
    try {
      const response = await fetch(`${apiUrl}/api/jobs/sync-jira-worklogs`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ month: selectedMonth })
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        // Fallback to inline sync if Cloud Run Job is not deployed in local dev
        await fetchJiraData(true);
        return;
      }

      window.dispatchEvent(new CustomEvent('open-job-logs', { detail: { jobId: data.jobId } }));
      setSuccessMessage(`Jira worklog sync job triggered for ${selectedMonth}.`);

      if (data.jobId) {
        const unsub = onSnapshot(doc(db, 'job_logs', data.jobId), (snapshot) => {
          if (snapshot.exists()) {
            const status = snapshot.data().status;
            if (status === 'completed' || status === 'failed') {
              fetchJiraData(false);
              setSyncing(false);
              unsub();
            }
          }
        });
      } else {
        setSyncing(false);
      }
    } catch (err) {
      setError(err.message);
      setSyncing(false);
    }
  };

  const handleSendToAfas = () => {
    setShowConfirmModal(true);
  };

  const executeSendToAfas = async () => {
    const recordsToSend = selectedRecords.size === 0
      ? filteredAndSortedRecords
      : filteredAndSortedRecords.filter((r) => selectedRecords.has(recordId(r)));

    setShowConfirmModal(false);
    setSendingToAfas(true);
    setSuccessMessage('');
    setError(null);

    try {
      const response = await fetch(`${apiUrl}/api/jobs/afas-jira-invoices`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ month: selectedMonth, records: recordsToSend })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(errorText(data, t, 'Failed to trigger Cloud Run Job'));
      }

      window.dispatchEvent(new CustomEvent('open-job-logs', { detail: { jobId: data.jobId } }));
      setSuccessMessage(`Job triggered successfully for ${recordsToSend.length} Jira support record(s).`);
      setSelectedRecords(new Set());

      if (data.jobId) {
        const unsub = onSnapshot(doc(db, 'job_logs', data.jobId), (snapshot) => {
          if (snapshot.exists()) {
            const status = snapshot.data().status;
            if (status === 'completed' || status === 'failed') {
              fetchJiraData();
              unsub();
            }
          }
        });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSendingToAfas(false);
    }
  };

  const toggleDebtor = (debtor) => {
    const next = new Set(selectedDebtors);
    if (next.has(debtor)) next.delete(debtor);
    else next.add(debtor);
    setSelectedDebtors(next);
  };

  const toggleInvoiceBy = (inv) => {
    const next = new Set(selectedInvoiceBy);
    if (next.has(inv)) next.delete(inv);
    else next.add(inv);
    setSelectedInvoiceBy(next);
  };

  const toggleAuthor = (author) => {
    const next = new Set(selectedAuthors);
    if (next.has(author)) next.delete(author);
    else next.add(author);
    setSelectedAuthors(next);
  };

  if (loading) {
    return (
      <div className="p-6 animate-pulse">
        <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
          <div>
            <div className="h-8 bg-gray-200 rounded w-64 mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-96"></div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="h-10 bg-gray-200 rounded w-32"></div>
            <div className="h-10 bg-gray-200 rounded w-32"></div>
            <div className="h-10 bg-gray-200 rounded w-64"></div>
          </div>
        </div>
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-primary opacity-85">
              <tr>
                {[1, 2, 3, 4, 5, 6, 7].map((i) => (
                  <th key={i} className="px-6 py-4">
                    <div className="h-3 bg-white/40 rounded w-full"></div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {[1, 2, 3, 4, 5].map((rowIdx) => (
                <tr key={rowIdx}>
                  <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-4"></div></td>
                  <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-36"></div></td>
                  <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-28"></div></td>
                  <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-64"></div></td>
                  <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-20"></div></td>
                  <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-20 ml-auto"></div></td>
                  <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-24 ml-auto"></div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  const activeFilterCount = selectedDebtors.size + selectedInvoiceBy.size + selectedAuthors.size;
  const isGroupedSort = sortConfig.key === 'debtor_id' || sortConfig.key === 'domain';

  return (
    <div className="p-6">
      <ReadOnlyNotice t={t} />

      <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
        <div className="flex items-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-8 w-8 mr-3 bg-primary text-white rounded-full p-1.5 flex-shrink-0 shadow-sm"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
            />
          </svg>
          <h2 className="text-2xl font-bold text-gray-900">{t?.admin_jira || 'Jira'}</h2>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <input
            type="month"
            className="border border-gray-300 rounded-md px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary text-gray-700"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
          />

          {canWrite && (
            <button
              onClick={handleSyncJira}
              disabled={syncing}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium border transition-colors ${
                syncing
                  ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                  : 'bg-white text-primary border-primary hover:bg-purple-50 shadow-sm'
              }`}
              title="Sync Jira issues and worklogs for the selected month into Firestore"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              {syncing ? 'Syncing...' : 'Sync Jira'}
            </button>
          )}

          {canWrite && (
            <button
              onClick={handleSendToAfas}
              disabled={sendingToAfas || filteredAndSortedRecords.length === 0}
              className={`px-4 py-2 rounded-md text-sm font-medium text-white transition-colors ${
                sendingToAfas || filteredAndSortedRecords.length === 0
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-primary hover:bg-opacity-90 shadow-sm'
              }`}
            >
              {sendingToAfas ? 'Sending...' : 'Send to AFAS'}
            </button>
          )}

          <button
            onClick={() => setShowFiltersModal(true)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            Filters
            {activeFilterCount > 0 && (
              <span className="bg-primary text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center ml-1">
                {activeFilterCount}
              </span>
            )}
          </button>

          <input
            type="text"
            placeholder="Search issue, domain, debtor, author..."
            className="border border-gray-300 rounded-md px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary w-64"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />

          <div className="bg-indigo-50 text-indigo-700 px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap">
            Showing: {filteredAndSortedRecords.length}
          </div>
        </div>
      </div>

      {/* View Mode Filter Tabs */}
      <div className="mb-4 flex flex-wrap items-center justify-end gap-1.5">
        <button
          type="button"
          onClick={() => setViewMode('ready_to_invoice')}
          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
            viewMode === 'ready_to_invoice'
              ? 'bg-primary text-white shadow-sm'
              : 'bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900 border border-gray-200'
          }`}
        >
          <span>Ready to Invoice</span>
          <span
            className={`text-[10px] px-1.5 py-0.2 rounded-full font-semibold ${
              viewMode === 'ready_to_invoice'
                ? 'bg-white/20 text-white'
                : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
            }`}
          >
            {counts.ready}
          </span>
        </button>

        <button
          type="button"
          onClick={() => setViewMode('all')}
          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
            viewMode === 'all'
              ? 'bg-primary text-white shadow-sm'
              : 'bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900 border border-gray-200'
          }`}
        >
          <span>All Records</span>
          <span
            className={`text-[10px] px-1.5 py-0.2 rounded-full font-semibold ${
              viewMode === 'all'
                ? 'bg-white/20 text-white'
                : 'bg-gray-100 text-gray-600'
            }`}
          >
            {counts.all}
          </span>
        </button>

        <button
          type="button"
          onClick={() => setViewMode('do_not_invoice')}
          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
            viewMode === 'do_not_invoice'
              ? 'bg-primary text-white shadow-sm'
              : 'bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900 border border-gray-200'
          }`}
        >
          <span>Do Not Invoice</span>
          <span
            className={`text-[10px] px-1.5 py-0.2 rounded-full font-semibold ${
              viewMode === 'do_not_invoice'
                ? 'bg-white/20 text-white'
                : 'bg-rose-50 text-rose-700 border border-rose-200'
            }`}
          >
            {counts.doNotInvoiceCount}
          </span>
        </button>

        <button
          type="button"
          onClick={() => setViewMode('unlinked')}
          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
            viewMode === 'unlinked'
              ? 'bg-primary text-white shadow-sm'
              : 'bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900 border border-gray-200'
          }`}
        >
          <span>Unlinked Accounts</span>
          <span
            className={`text-[10px] px-1.5 py-0.2 rounded-full font-semibold ${
              viewMode === 'unlinked'
                ? 'bg-white/20 text-white'
                : 'bg-amber-50 text-amber-700 border border-amber-200'
            }`}
          >
            {counts.unlinked}
          </span>
        </button>

        <button
          type="button"
          onClick={() => setViewMode('internal')}
          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
            viewMode === 'internal'
              ? 'bg-primary text-white shadow-sm'
              : 'bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900 border border-gray-200'
          }`}
        >
          <span>Internal (15802)</span>
          <span
            className={`text-[10px] px-1.5 py-0.2 rounded-full font-semibold ${
              viewMode === 'internal'
                ? 'bg-white/20 text-white'
                : 'bg-blue-50 text-blue-700 border border-blue-200'
            }`}
          >
            {counts.internal}
          </span>
        </button>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 p-4 rounded-md border border-red-200">
          <p className="text-red-800 text-sm font-medium">{error}</p>
        </div>
      )}

      {successMessage && (
        <div className="mb-4 bg-green-50 p-4 rounded-md">
          <p className="text-green-800 text-sm font-medium">{successMessage}</p>
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-primary text-white">
            <tr>
              <th scope="col" className="px-6 py-3 text-left">
                {canWrite && (
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                    onChange={handleSelectAll}
                    checked={
                      filteredAndSortedRecords.length > 0 &&
                      selectedRecords.size === filteredAndSortedRecords.length
                    }
                  />
                )}
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-opacity-90"
                onClick={() => handleSort('debtor_id')}
              >
                Customer / Debtor {sortConfig.key === 'debtor_id' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-opacity-90"
                onClick={() => handleSort('key')}
              >
                Issue / Worklog {sortConfig.key === 'key' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-opacity-90"
                onClick={() => handleSort('text')}
              >
                Description {sortConfig.key === 'text' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-opacity-90"
                onClick={() => handleSort('po_number')}
              >
                PO Number {sortConfig.key === 'po_number' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-opacity-90"
                onClick={() => handleSort('cost')}
              >
                Cost {sortConfig.key === 'cost' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-opacity-90"
                onClick={() => handleSort('created_order_id')}
              >
                Created Order ID {sortConfig.key === 'created_order_id' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredAndSortedRecords.map((r, index) => {
              const id = recordId(r);
              const gKey = groupKeyForRecord(r);
              const prevGKey = index > 0 ? groupKeyForRecord(filteredAndSortedRecords[index - 1]) : null;
              const nextGKey = index < filteredAndSortedRecords.length - 1 ? groupKeyForRecord(filteredAndSortedRecords[index + 1]) : null;

              const showGroupHeader = isGroupedSort && (index === 0 || gKey !== prevGKey);
              const isLastInGroup = isGroupedSort && (index === filteredAndSortedRecords.length - 1 || gKey !== nextGKey);

              let subtotal = 0, vatPercentage = 0, vatAmount = 0, grandTotal = 0, vatLabel = '0%';
              let totalGroupHours = 0;
              if (isLastInGroup) {
                const groupRecords = filteredAndSortedRecords.filter(
                  (item) => groupKeyForRecord(item) === gKey
                );
                subtotal = groupRecords.reduce((sum, item) => sum + Number(item.cost ?? item.price ?? 0), 0);
                totalGroupHours = groupRecords.reduce(
                  (sum, item) => sum + Number(item.timeSpentHours ?? ((item.timeSpentSeconds || 0) / 3600)),
                  0
                );

                const vatDetails = getVatDetails({
                  administratie: r.invoice_by,
                  country: r.country,
                  vatCode: r.vatCode,
                });
                vatPercentage = vatDetails.vatRate;
                vatAmount = subtotal * vatPercentage;
                grandTotal = subtotal + vatAmount;
                vatLabel = vatDetails.vatCode ? `(${vatDetails.vatCode}, ${vatDetails.vatPercent}%)` : `(${vatDetails.vatPercent}%)`;
              }

              const debtorId = String(r.debtor_id || r.afas_debtor_number || '').trim();
              const debtorName = r.debtor_name || r.invoice_by_name || r.customer_name || '';
              const costNum = Number(r.cost ?? r.price ?? 0);
              const hoursNum = Number(r.timeSpentHours ?? ((r.timeSpentSeconds || 0) / 3600));
              const dni = isDoNotInvoice(r);

              const groupTitle = debtorId
                ? `${debtorName ? `${debtorName} ` : ''}(${debtorId}${r.domain ? ` • ${r.domain}` : ''})`
                : `${r.domain || 'Unlinked'} (No Debtor ID)`;

              return (
                <React.Fragment key={id}>
                  {showGroupHeader && (
                    <tr className="bg-gray-100 border-y border-gray-200">
                      <td className="px-6 py-2">
                        {canWrite && (
                          <input
                            type="checkbox"
                            className="rounded border-gray-400 text-primary shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                            checked={isGroupFullySelected(gKey)}
                            onChange={(e) => handleSelectGroup(gKey, e.target.checked)}
                          />
                        )}
                      </td>
                      <td colSpan="6" className="px-6 py-2 text-sm font-bold text-gray-800">
                        Select all "{groupTitle}"
                      </td>
                    </tr>
                  )}

                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {canWrite && (
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                          checked={selectedRecords.has(id)}
                          onChange={() => handleSelectRecord(id)}
                        />
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-medium text-gray-900">{r.domain || '-'}</span>
                        {debtorId ? (
                          <Link
                            to={`/admin/customers/${debtorId}`}
                            className="inline-block px-1.5 py-0.5 rounded text-xs font-bold text-purple-700 bg-purple-50 hover:bg-purple-100 hover:text-purple-900 hover:underline border border-purple-200 transition-colors cursor-pointer"
                            title={debtorName || debtorId}
                          >
                            {debtorId}
                          </Link>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                            Unlinked
                          </span>
                        )}
                      </div>
                      {debtorName && (
                        debtorId ? (
                          <Link
                            to={`/admin/customers/${debtorId}`}
                            className="block text-xs text-gray-500 hover:text-gray-800 hover:underline truncate max-w-[240px] mt-0.5 cursor-pointer"
                            title={debtorName}
                          >
                            {debtorName}
                          </Link>
                        ) : (
                          <div
                            className="text-xs text-gray-500 font-sans mt-0.5 truncate max-w-[240px]"
                            title={debtorName}
                          >
                            {debtorName}
                          </div>
                        )
                      )}
                      {r.reporterEmail && (
                        <div className="text-[11px] text-gray-400 truncate max-w-[240px]" title={r.reporterEmail}>
                          {r.reporterEmail}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-indigo-700">{r.key}</span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-purple-50 text-purple-700 border border-purple-200">
                          {hoursNum.toFixed(2)} h
                        </span>
                        {dni && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-rose-100 text-rose-800">
                            Do Not Invoice
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        {r.authorPicture ? (
                          <img
                            src={r.authorPicture}
                            alt={r.authorName || r.emailAddress || 'Author'}
                            className="w-6 h-6 rounded-full object-cover border border-gray-200 flex-shrink-0"
                            referrerPolicy="no-referrer"
                          />
                        ) : (
                          <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 font-bold text-xs flex-shrink-0">
                            {(r.emailAddress || r.authorName || '?').charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div className="flex flex-col leading-tight">
                          <div className="text-xs text-gray-500">
                            {r.emailAddress || r.authorName || 'Unknown author'}
                          </div>
                          {r.created_date && (
                            <div className="text-[11px] text-gray-400">
                              {r.created_date}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-normal text-sm text-gray-600 min-w-[260px]">
                      {r.factuurtekst || r.text}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-500">
                      {r.po_number || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                      {costNum.toFixed(2)} {r.currency || 'EUR'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {r.created_order_id ? (
                        <div className="flex items-center justify-end gap-1.5">
                          {r.isCreditNote && (
                            <span
                              className="inline-flex items-center justify-center w-4 h-4 bg-red-500 text-white rounded-full font-bold text-[10px] leading-none select-none"
                              title="Credit Note"
                            >
                              -
                            </span>
                          )}
                          <Link
                            to={`/admin/afas-transactions/${r.created_order_id}`}
                            className="text-indigo-600 font-semibold hover:text-indigo-800 hover:underline transition-colors"
                          >
                            {r.created_order_id}
                          </Link>
                        </div>
                      ) : (
                        '-'
                      )}
                    </td>
                  </tr>

                  {isLastInGroup && (
                    <>
                      <tr className="bg-gray-50 border-t border-gray-200">
                        <td colSpan="5" className="px-6 py-2 text-right text-sm font-bold text-gray-500">
                          Subtotal ({totalGroupHours.toFixed(2)} h × €{r.hourRate || 87}/h)
                        </td>
                        <td className="px-6 py-2 text-right text-sm font-bold text-gray-700">
                          {subtotal.toFixed(2)} EUR
                        </td>
                        <td></td>
                      </tr>
                      <tr className="bg-gray-50">
                        <td colSpan="5" className="px-6 py-2 text-right text-sm font-bold text-gray-500">
                          VAT {vatLabel}
                        </td>
                        <td className="px-6 py-2 text-right text-sm font-bold text-gray-700">
                          {vatAmount.toFixed(2)} EUR
                        </td>
                        <td></td>
                      </tr>
                      <tr className="bg-blue-50/50 border-b-2 border-gray-300">
                        <td colSpan="5" className="px-6 py-3 text-right text-sm font-black text-gray-800 uppercase tracking-wider">
                          Total Invoice Amount
                        </td>
                        <td className="px-6 py-3 text-right text-sm font-black text-indigo-700">
                          {grandTotal.toFixed(2)} EUR
                        </td>
                        <td></td>
                      </tr>
                    </>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>

        {filteredAndSortedRecords.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            {records.length === 0
              ? `No synced Jira worklogs found in Firestore for ${selectedMonth}. Click "Sync Jira" to extract worklogs for this month.`
              : viewMode === 'ready_to_invoice'
              ? `No billable Jira worklog records found for ${selectedMonth}.`
              : viewMode === 'do_not_invoice'
              ? `No "Do Not Invoice" records found for ${selectedMonth}.`
              : viewMode === 'unlinked'
              ? `No unlinked Jira records found for ${selectedMonth}.`
              : viewMode === 'internal'
              ? `No internal (15802) records found for ${selectedMonth}.`
              : `No Jira billing data matching filters for ${selectedMonth}.`}
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900 bg-opacity-50 transition-opacity">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 animate-in fade-in zoom-in-95 duration-200">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Process Jira Support Invoices?</h3>
            <p className="text-gray-600 mb-6 text-sm">
              {selectedRecords.size === 0
                ? `No records selected. Are you sure you want to invoice all ${filteredAndSortedRecords.length} displayed Jira record(s)?`
                : `Are you sure you want to invoice ${selectedRecords.size} selected Jira support record(s)?`}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={executeSendToAfas}
                className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary shadow-sm transition-colors"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filters Modal */}
      {showFiltersModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900 bg-opacity-50 transition-opacity">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl p-6 max-h-[90vh] flex flex-col animate-in fade-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">Filter Jira Records</h3>
              <button onClick={() => setShowFiltersModal(false)} className="text-gray-400 hover:text-gray-500 focus:outline-none">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="overflow-y-auto flex-1 pr-2 space-y-6">
              {uniqueDebtors.length > 0 && (
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-sm font-semibold text-gray-700">Debtor:</span>
                    {selectedDebtors.size > 0 && (
                      <button
                        onClick={() => setSelectedDebtors(new Set())}
                        className="text-gray-400 hover:text-red-500 hover:bg-red-50 p-1 rounded-full transition-colors focus:outline-none"
                        title="Clear Debtor Filters"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {uniqueDebtors.map((debtor) => {
                      const isSelected = selectedDebtors.has(debtor);
                      return (
                        <button
                          key={debtor}
                          onClick={() => toggleDebtor(debtor)}
                          className={`px-3.5 py-1.5 rounded-full text-xs font-bold transition-all duration-200 border select-none ${
                            isSelected
                              ? 'bg-primary text-white border-primary shadow-sm shadow-primary/20 scale-105'
                              : 'bg-white text-primary border-primary/20 hover:bg-primary/5 hover:border-primary/40'
                          }`}
                        >
                          {debtor}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {uniqueInvoiceBy.length > 0 && (
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-sm font-semibold text-gray-700">Invoice By (Administration):</span>
                    {selectedInvoiceBy.size > 0 && (
                      <button
                        onClick={() => setSelectedInvoiceBy(new Set())}
                        className="text-gray-400 hover:text-red-500 hover:bg-red-50 p-1 rounded-full transition-colors focus:outline-none"
                        title="Clear Invoice By Filters"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {uniqueInvoiceBy.map((inv) => {
                      const isSelected = selectedInvoiceBy.has(inv);
                      return (
                        <button
                          key={inv}
                          onClick={() => toggleInvoiceBy(inv)}
                          className={`px-3.5 py-1.5 rounded-full text-xs font-bold transition-all duration-200 border select-none ${
                            isSelected
                              ? 'bg-teal-600 text-white border-teal-600 shadow-sm shadow-teal-600/20 scale-105'
                              : 'bg-white text-teal-700 border-teal-200 hover:bg-teal-50/50 hover:border-teal-300'
                          }`}
                        >
                          {inv}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {uniqueAuthors.length > 0 && (
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-sm font-semibold text-gray-700">Worklog Author:</span>
                    {selectedAuthors.size > 0 && (
                      <button
                        onClick={() => setSelectedAuthors(new Set())}
                        className="text-gray-400 hover:text-red-500 hover:bg-red-50 p-1 rounded-full transition-colors focus:outline-none"
                        title="Clear Author Filters"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {uniqueAuthors.map((author) => {
                      const isSelected = selectedAuthors.has(author);
                      return (
                        <button
                          key={author}
                          onClick={() => toggleAuthor(author)}
                          className={`px-3.5 py-1.5 rounded-full text-xs font-bold transition-all duration-200 border select-none ${
                            isSelected
                              ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm shadow-indigo-600/20 scale-105'
                              : 'bg-white text-indigo-700 border-indigo-200 hover:bg-indigo-50/50 hover:border-indigo-300'
                          }`}
                        >
                          {author}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end gap-3 pt-4 border-t border-gray-200">
              {activeFilterCount > 0 && (
                <button
                  onClick={() => {
                    setSelectedDebtors(new Set());
                    setSelectedInvoiceBy(new Set());
                    setSelectedAuthors(new Set());
                  }}
                  className="px-4 py-2 text-sm font-medium text-red-700 bg-white border border-red-200 rounded-md hover:bg-red-50 transition-colors"
                >
                  Clear All
                </button>
              )}
              <button
                onClick={() => setShowFiltersModal(false)}
                className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-opacity-90 transition-colors shadow-sm"
              >
                Close & Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminJira;

